import React from 'react'
import { Button, IconButton } from '@chakra-ui/react'
import { Box, HStack } from '@chakra-ui/react'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'

interface Props {
    page: number
    setPage: (page: number) => void
    count: number
    /** More results exist past `count`, so "next" stays enabled on the last page. */
    isCountCapped?: boolean
    pageSize: number
}

export default function PageSelect({
    page,
    setPage,
    count,
    isCountCapped = false,
    pageSize,
}: Props) {
    const totalPages = Math.ceil((count || 1) / (pageSize || 1))

    const buttonProps = {
        size: 'xs',
        w: '28px',
        h: '28px',
        variant: 'ghost',
    }

    // Ellipsis component
    const Ellipsis = () => (
        <Box fontSize="sm" color="gray.500" mx={1}>
            ...
        </Box>
    )

    // Page button component
    const PageButton = ({ pageNum }: { pageNum: number }) => (
        <Button
            onClick={() => setPage(pageNum)}
            size="xs"
            w="28px"
            h="28px"
            variant={page === pageNum ? 'subtle' : 'ghost'}
            colorScheme={page === pageNum ? 'brand' : 'gray'}
        >
            {pageNum + 1}
        </Button>
    )

    return (
        <HStack spacing={0.5} justifyContent="center">
            <IconButton
                isDisabled={page === 0}
                icon={<ChevronLeftIcon />}
                aria-label="Previous page"
                onClick={() => setPage(page - 1)}
                {...buttonProps}
            />
            <PageButton pageNum={0} />
            {page > 2 && !(page === totalPages - 1 && totalPages <= 4) && (
                <Ellipsis />
            )}
            {page === totalPages - 1 && totalPages > 3 && (
                <PageButton pageNum={totalPages - 3} />
            )}
            {page > 1 && <PageButton pageNum={page - 1} />}
            {page > 0 && page < totalPages - 1 && <PageButton pageNum={page} />}
            {page < totalPages - 2 && <PageButton pageNum={page + 1} />}
            {page === 0 && totalPages > 3 && <PageButton pageNum={2} />}
            {page < totalPages - 3 && !(page === 0 && totalPages <= 4) && (
                <Ellipsis />
            )}
            {totalPages !== 1 && <PageButton pageNum={totalPages - 1} />}
            <IconButton
                aria-label="Next page"
                icon={<ChevronRightIcon />}
                onClick={() => setPage(page + 1)}
                isDisabled={page === totalPages - 1 && !isCountCapped}
                {...buttonProps}
            />
        </HStack>
    )
}
